import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, FileText, BookOpen, Newspaper, Users, Inbox, Settings as SettingsIcon, LogOut, Layers, ExternalLink, DollarSign, Handshake, Image, Sparkles, Home as HomeIcon, Palette, Globe, Briefcase, Search } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const NAV_GROUPS = [
    {
        label: "Overview",
        items: [{ to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true }],
    },
    {
        label: "Site",
        items: [
            { to: "/admin/identity", label: "Site Identity", icon: Globe },
            { to: "/admin/homepage", label: "Homepage", icon: HomeIcon },
            { to: "/admin/pages", label: "About Page", icon: FileText },
            { to: "/admin/header-footer", label: "Header & Footer", icon: Sparkles },
            { to: "/admin/theme", label: "Theme & Colors", icon: Palette },
        ],
    },
    {
        label: "Content",
        items: [
            { to: "/admin/programs", label: "Programs", icon: Layers },
            { to: "/admin/news", label: "News & Stories", icon: Newspaper },
            { to: "/admin/resources", label: "Resources", icon: BookOpen },
            { to: "/admin/gallery", label: "Gallery", icon: Image },
            { to: "/admin/partners", label: "Partners", icon: Handshake },
            { to: "/admin/leadership", label: "Leadership", icon: Users },
            { to: "/admin/careers", label: "Careers / Jobs", icon: Briefcase },
        ],
    },
    {
        label: "People & funds",
        items: [
            { to: "/admin/inquiries", label: "Inquiries", icon: Inbox },
            { to: "/admin/donations", label: "Donations", icon: DollarSign },
            { to: "/admin/seo", label: "Per-Page SEO", icon: Search },
            { to: "/admin/settings", label: "Donations & SEO", icon: SettingsIcon },
        ],
    },
];

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();

    return (
        <div className="min-h-screen flex bg-muted/30" data-testid="admin-layout">
            <aside className="w-64 bg-card border-r border-border flex flex-col">
                <div className="px-6 py-6 border-b border-border">
                    <div className="font-display text-xl font-semibold">YACRRD</div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-1">Admin Console</div>
                </div>
                <nav className="flex-1 py-4 px-3 space-y-4 overflow-y-auto">
                    {NAV_GROUPS.map((group) => (
                        <div key={group.label}>
                            <div className="px-3 mb-1 text-[10px] uppercase tracking-[0.22em] text-muted-foreground/70">{group.label}</div>
                            <div className="space-y-1">
                                {group.items.map(({ to, label, icon: Icon, end }) => (
                                    <NavLink
                                        key={to}
                                        to={to}
                                        end={end}
                                        className={({ isActive }) =>
                                            `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                                                isActive ? "bg-primary text-primary-foreground" : "text-foreground/80 hover:bg-muted"
                                            }`
                                        }
                                        data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                                    >
                                        <Icon size={16} /> {label}
                                    </NavLink>
                                ))}
                            </div>
                        </div>
                    ))}
                </nav>
                <div className="p-3 border-t border-border space-y-1">
                    <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-foreground/70 hover:bg-muted">
                        <ExternalLink size={14} /> View Site
                    </a>
                    <button
                        onClick={async () => { await logout(); nav("/admin/login"); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-destructive hover:bg-destructive/10"
                        data-testid="admin-logout"
                    >
                        <LogOut size={14} /> Sign out
                    </button>
                </div>
            </aside>
            <main className="flex-1 overflow-x-hidden">
                <div className="px-8 py-5 border-b border-border bg-card flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">Signed in as <span className="text-foreground font-medium">{user?.email}</span></div>
                </div>
                <div className="p-8">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
